// SA1 — Lista de Tenants (+ SA3 onboarding en dialog, + SA2 detalle en dialog).

import { useState } from 'react';
import { toast } from 'sonner';
import type { SuperTenantResumen } from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Spinner } from '../../components/ui/spinner';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { SuperAdminSidebar } from '../../components/layout/SuperAdminSidebar';
import {
  useSuperTenants,
  useSuperTenantDetalle,
  useSuperTenantUsuarios,
  useSetPermisoConfigTenant,
  useResetPasswordSA,
  useCreateSuperTenant,
} from '../../lib/api/queries';

export function SA1TenantsPage() {
  const { data, isLoading, isError } = useSuperTenants();
  const [onboarding, setOnboarding] = useState(false);
  const [detalleId, setDetalleId] = useState<string | null>(null);

  return (
    <RoleLayout sidebar={<SuperAdminSidebar />} roleLabel="Super Admin · Jidoka">
      <div className="mx-auto flex max-w-4xl flex-col gap-4">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-medium text-fg">Tenants</h1>
            <p className="mt-1 text-[11px] text-fg-tertiary">Todos los clientes de Jidoka</p>
          </div>
          <Button onClick={() => setOnboarding(true)}>Nuevo tenant</Button>
        </header>
        {isLoading || !data ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            {isError ? <span className="text-sm text-crit">No se pudo cargar.</span> : <Spinner />}
          </div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Tenant</TH>
                <TH>NIT</TH>
                <TH>Plan</TH>
                <TH align="right">Plantas</TH>
                <TH align="right">Usuarios</TH>
                <TH align="center">Estado</TH>
                <TH align="right" />
              </TR>
            </THead>
            <TBody>
              {data.tenants.map((t) => (
                <TR key={t.id}>
                  <TD className="font-medium text-fg">{t.nombre}</TD>
                  <TD className="text-fg-secondary">{t.nit}</TD>
                  <TD className="text-fg-secondary">{t.plan}</TD>
                  <TD align="right" className="tabular-nums">
                    {t.plantas_count}
                  </TD>
                  <TD align="right" className="tabular-nums">
                    {t.usuarios_count}
                  </TD>
                  <TD align="center">
                    <Badge tone={t.activo ? 'ok' : 'neutral'}>
                      {t.activo ? 'Activo' : 'Inactivo'}
                    </Badge>
                  </TD>
                  <TD align="right">
                    <Button size="sm" variant="ghost" onClick={() => setDetalleId(t.id)}>
                      Ver
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>
      {onboarding && <OnboardingDialog onClose={() => setOnboarding(false)} />}
      {detalleId && <DetalleDialog id={detalleId} onClose={() => setDetalleId(null)} />}
    </RoleLayout>
  );
}

function OnboardingDialog({ onClose }: { onClose: () => void }) {
  const crear = useCreateSuperTenant();
  const [f, setF] = useState({
    nombre: '',
    nit: '',
    contacto_email: '',
    plan: 'mvp',
    admin_email: '',
    admin_nombre: '',
    admin_apellido: '',
  });
  const ok =
    f.nombre && f.nit && f.contacto_email && f.admin_email && f.admin_nombre && f.admin_apellido;
  return (
    <Dialog open onClose={onClose} className="max-w-lg">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Onboarding de tenant nuevo</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <span className="text-[10px] font-medium uppercase tracking-wide text-fg-tertiary">
            Tenant
          </span>
          <Campo label="Nombre" value={f.nombre} onChange={(v) => setF({ ...f, nombre: v })} />
          <div className="grid grid-cols-2 gap-3">
            <Campo label="NIT" value={f.nit} onChange={(v) => setF({ ...f, nit: v })} />
            <Campo label="Plan" value={f.plan} onChange={(v) => setF({ ...f, plan: v })} />
          </div>
          <Campo
            label="Email de contacto"
            value={f.contacto_email}
            onChange={(v) => setF({ ...f, contacto_email: v })}
          />
          <span className="mt-1 text-[10px] font-medium uppercase tracking-wide text-fg-tertiary">
            Primer Admin del tenant
          </span>
          <Campo
            label="Email del admin"
            value={f.admin_email}
            onChange={(v) => setF({ ...f, admin_email: v })}
          />
          <div className="grid grid-cols-2 gap-3">
            <Campo
              label="Nombre"
              value={f.admin_nombre}
              onChange={(v) => setF({ ...f, admin_nombre: v })}
            />
            <Campo
              label="Apellido"
              value={f.admin_apellido}
              onChange={(v) => setF({ ...f, admin_apellido: v })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={crear.isPending || !ok}
            onClick={() =>
              crear.mutate(
                {
                  nombre: f.nombre.trim(),
                  nit: f.nit.trim(),
                  contacto_email: f.contacto_email.trim(),
                  plan: f.plan.trim() || 'mvp',
                  admin_email: f.admin_email.trim(),
                  admin_nombre: f.admin_nombre.trim(),
                  admin_apellido: f.admin_apellido.trim(),
                },
                {
                  onSuccess: (res) => {
                    // Respuesta del backend (SA3): { tenant_id, admin_user_id, invitado }.
                    // okSchema es passthrough → leemos invitado defensivamente.
                    const r = res as { invitado?: boolean };
                    const admin = f.admin_email.trim();
                    if (r.invitado) {
                      toast.success(`Tenant creado — invitación enviada al admin ${admin}`);
                    } else {
                      toast.success(`Tenant creado — el admin ${admin} ya tenía cuenta`);
                    }
                    onClose();
                  },
                  onError: (e: unknown) => toast.error(e instanceof Error ? e.message : 'Error'),
                },
              )
            }
          >
            Crear tenant
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetalleDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isLoading } = useSuperTenantDetalle(id);
  return (
    <Dialog open onClose={onClose} className="max-w-xl">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{data?.tenant.nombre ?? 'Detalle del tenant'}</DialogTitle>
        </DialogHeader>
        {isLoading || !data ? (
          <div className="flex min-h-32 items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-col gap-4 text-sm">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Usuarios" value={data.totales.usuarios} />
              <Stat label="Líneas" value={data.totales.lineas} />
              <Stat label="Capturas 30d" value={data.totales.capturas_30d} />
            </div>
            <div>
              <span className="text-[10px] font-medium uppercase tracking-wide text-fg-tertiary">
                Usuarios por rol
              </span>
              <div className="mt-1 flex flex-wrap gap-2">
                {data.usuarios_por_rol.map((r) => (
                  <Badge key={r.rol} tone="neutral">
                    {r.rol}: {r.total}
                  </Badge>
                ))}
              </div>
            </div>
            <div>
              <span className="text-[10px] font-medium uppercase tracking-wide text-fg-tertiary">
                Plantas ({data.plantas.length})
              </span>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {data.plantas.map((p) => (
                  <span
                    key={p.id}
                    className={`rounded border px-2 py-0.5 text-xs ${p.activa ? 'border-border text-fg' : 'border-border text-fg-tertiary line-through'}`}
                  >
                    {p.nombre}
                  </span>
                ))}
              </div>
            </div>
            <PermisosConfigSection tenantId={id} />
          </div>
        )}
        <DialogFooter>
          <Button variant="primary" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded border border-border p-2 text-center">
      <div className="text-lg font-medium tabular-nums text-fg">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-fg-tertiary">{label}</div>
    </div>
  );
}

function Campo({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

// =============================================================================
// PermisosConfigSection — permiso `puede_configurar_tenant` por directivo.
// Decisión 2026-06-11: un solo rol estratégico (directivo); Jidoka define
// quiénes pueden configurar el tenant (A1-A6). admin_tenant legacy lo tiene
// implícito por rol y no se gestiona acá.
// =============================================================================
function PermisosConfigSection({ tenantId }: { tenantId: string }) {
  const usuarios = useSuperTenantUsuarios(tenantId);
  const setPermiso = useSetPermisoConfigTenant();
  const resetPass = useResetPasswordSA();
  const directivos = (usuarios.data?.usuarios ?? []).filter((u) => u.rol === 'directivo');
  // Reset de contraseña (operación de emergencia, spec SA §6): input inline
  // con confirmación explícita. Auditado en el backend SIN la contraseña.
  const [resetUserId, setResetUserId] = useState<string | null>(null);
  const [nuevaPass, setNuevaPass] = useState('');

  return (
    <div>
      <span className="text-[10px] font-medium uppercase tracking-wide text-fg-tertiary">
        Permiso de configuración (directivos)
      </span>
      {usuarios.isLoading ? (
        <p className="mt-1 text-xs text-fg-tertiary">Cargando…</p>
      ) : directivos.length === 0 ? (
        <p className="mt-1 text-xs text-fg-tertiary">Este tenant no tiene directivos.</p>
      ) : (
        <ul className="mt-1 flex flex-col gap-1.5">
          {directivos.map((u) => (
            <li
              key={u.user_id}
              className="flex items-center justify-between gap-2 rounded border border-border px-2.5 py-1.5"
            >
              <div className="min-w-0">
                <p className="truncate text-xs font-medium text-fg">
                  {u.nombre} {u.apellido}
                </p>
                <p className="truncate text-[11px] text-fg-tertiary">{u.email}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {resetUserId === u.user_id ? (
                  <span className="flex items-center gap-1.5">
                    <Input
                      type="text"
                      placeholder="Nueva contraseña (≥8)"
                      value={nuevaPass}
                      onChange={(e) => setNuevaPass(e.target.value)}
                      className="h-7 w-44 text-xs"
                    />
                    <Button
                      size="sm"
                      disabled={resetPass.isPending || nuevaPass.length < 8}
                      onClick={() => {
                        resetPass.mutate(
                          { tenantId, userId: u.user_id, password: nuevaPass },
                          {
                            onSuccess: () => {
                              toast.success(`Contraseña de ${u.nombre} reseteada`);
                              setResetUserId(null);
                              setNuevaPass('');
                            },
                            onError: (e) =>
                              toast.error('No se pudo resetear', {
                                description: e instanceof Error ? e.message : undefined,
                              }),
                          },
                        );
                      }}
                    >
                      {resetPass.isPending ? '…' : 'Confirmar'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setResetUserId(null);
                        setNuevaPass('');
                      }}
                    >
                      ✕
                    </Button>
                  </span>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setResetUserId(u.user_id)}>
                    Reset pass
                  </Button>
                )}
                <Button
                  size="sm"
                  variant={u.puede_configurar_tenant ? 'primary' : 'outline'}
                  disabled={setPermiso.isPending}
                  onClick={() => {
                    setPermiso.mutate(
                      {
                        tenantId,
                        userId: u.user_id,
                        puede: !u.puede_configurar_tenant,
                      },
                      {
                        onSuccess: (r) =>
                          toast.success(
                            r.puede_configurar_tenant
                              ? `${u.nombre} ahora puede configurar el tenant`
                              : `Permiso revocado a ${u.nombre}`,
                          ),
                        onError: (e) =>
                          toast.error('No se pudo cambiar el permiso', {
                            description: e instanceof Error ? e.message : undefined,
                          }),
                      },
                    );
                  }}
                >
                  {u.puede_configurar_tenant ? 'Puede configurar' : 'Sin permiso'}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
